'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { IMPLANTACAO_ABERTA, IMPLANTACAO_PARAM } from '@/domain/implantacao/helpers/implantacaoItens';
import { PERMISSION } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { cn } from '@/lib/utils';

/** A lista que mostra item por item o que falta validar, já aberta. */
const IMPLANTACAO_HREF = `/settings?${IMPLANTACAO_PARAM}=${IMPLANTACAO_ABERTA}`;

interface HomeImplantacaoLinhaProps {
  /** Quantos itens ainda pedem validação; zero tira a linha da tela. */
  total: number;
  /** A leitura ainda não voltou, ou falhou: a linha não dá notícia que não tem. */
  silenciar: boolean;
  className?: string;
}

/**
 * Uma LINHA, não um cartão: o que falta validar é tarefa de implantação, e ela agora
 * mora inteira no cartão Implantação das Configurações.
 *
 * Na Início ela ocupava a primeira dobra com uma fila de atalhos que repetia, item por
 * item, a mesma lista daquela aba. Quem abre a Início de manhã quer o que fazer HOJE na
 * produção; a implantação acontece uma vez e some. Sobra o tamanho dela e o caminho.
 *
 * Só quem pode ALTERAR as configurações a vê: ela leva a um cartão que só essa pessoa
 * tem, e um atalho para uma tela que recusa a entrada não é um atalho.
 */
export function HomeImplantacaoLinha({ total, silenciar, className }: HomeImplantacaoLinhaProps) {
  const { t } = useTranslation('common');
  const { can } = usePermissions();

  if (silenciar || total <= 0 || !can(PERMISSION.CONFIGURACOES_EDIT)) {
    return null;
  }

  return (
    <p className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground-muted', className)}>
      <MaterialSymbol icon="checklist" size={16} className="shrink-0 text-foreground-subtle" aria-hidden />
      {t('home.implantacao.linha', { count: total })}
      <LinkButton href={IMPLANTACAO_HREF} variant="text" tone="brand" size="sm" className="normal-case">
        {t('home.implantacao.abrir')}
        <MaterialSymbol icon="arrow_forward" size={16} />
      </LinkButton>
    </p>
  );
}
